/**
 * SFT (Semi-Fungible Token) Utilities
 *
 * Helper functions for detecting, fetching, and working with SFTs on Klever blockchain.
 */

import axios from 'axios';

const KLEVER_API_BASE = 'https://api.mainnet.klever.org/v1.0';

/**
 * Determines if an asset is an SFT (Semi-Fungible Token)
 * SFTs have assetType === 'SemiFungible' in Klever API
 * @param {Object} asset - Asset object from Klever API
 * @returns {boolean} - True if asset is SFT
 */
export const isSFT = (asset) => {
  if (!asset) return false;

  // Check assetType field - this is the ONLY reliable method
  // Klever uses: 'NonFungible' for NFTs, 'SemiFungible' for SFTs
  const assetType = asset.assetType?.toLowerCase() || '';
  return assetType === 'semifungible' || assetType === 'sft';
};

/**
 * Determines if an asset is an NFT (Non-Fungible Token)
 * NFTs have assetType === 'NonFungible' in Klever API
 * @param {Object} asset - Asset object from Klever API
 * @returns {boolean} - True if asset is NFT
 */
export const isNFT = (asset) => {
  if (!asset) return false;

  // Check assetType field - this is the ONLY reliable method
  // Klever uses: 'NonFungible' for NFTs
  const assetType = asset.assetType?.toLowerCase() || '';
  return assetType === 'nonfungible' || assetType === 'nft';
};

/**
 * Fetches all SFTs in a collection
 * @param {string} collectionId - Collection ID
 * @returns {Promise<Array>} - Array of SFT assets
 */
export const fetchSFTCollection = async (collectionId) => {
  try {
    const response = await axios.get(`${KLEVER_API_BASE}/assets/sft/${collectionId}`);
    return response.data?.data?.assets || [];
  } catch (error) {
    console.error('Error fetching SFT collection:', error);
    return [];
  }
};

/**
 * Fetches a specific SFT by collection ID and nonce
 * @param {string} collectionId - Collection ID
 * @param {number} nonce - Token nonce
 * @returns {Promise<Object|null>} - SFT asset or null
 */
export const fetchSFTByNonce = async (collectionId, nonce) => {
  try {
    const response = await axios.get(`${KLEVER_API_BASE}/assets/sft/${collectionId}/${nonce}`);
    return response.data?.data?.asset || null;
  } catch (error) {
    console.error(`Error fetching SFT ${collectionId}/${nonce}:`, error);
    return null;
  }
};

/**
 * Fetches asset info (works for both NFTs and SFTs)
 * @param {string} assetId - Full asset ID (COLLECTION/NONCE)
 * @returns {Promise<Object|null>} - Asset object or null
 */
export const fetchAsset = async (assetId) => {
  try {
    const response = await axios.get(`${KLEVER_API_BASE}/assets/${assetId}`);
    return response.data?.data?.asset || null;
  } catch (error) {
    console.error(`Error fetching asset ${assetId}:`, error);
    return null;
  }
};

/**
 * Fetches holders of a specific SFT
 * @param {string} assetId - Full asset ID (COLLECTION/NONCE)
 * @returns {Promise<Array>} - Array of holder objects
 */
export const fetchSFTHolders = async (assetId) => {
  try {
    const response = await axios.get(`${KLEVER_API_BASE}/assets/holders/${assetId}`);
    return response.data?.data?.holders || [];
  } catch (error) {
    console.error(`Error fetching SFT holders for ${assetId}:`, error);
    return [];
  }
};

/**
 * Fetches all assets (NFTs + SFTs) owned by an address in a collection
 * @param {string} address - Wallet address
 * @param {string} collectionId - Collection ID
 * @returns {Promise<Array>} - Array of assets
 */
export const fetchAddressCollection = async (address, collectionId) => {
  try {
    const response = await axios.get(
      `${KLEVER_API_BASE}/address/${address}/collection/${collectionId}`
    );
    return response.data?.data?.assets || [];
  } catch (error) {
    console.error(`Error fetching collection ${collectionId} for address ${address}:`, error);
    return [];
  }
};

/**
 * Gets the user's balance of a specific SFT
 * @param {string} address - Wallet address
 * @param {string} assetId - Full asset ID (COLLECTION/NONCE)
 * @returns {Promise<number>} - Balance amount
 */
export const getSFTBalance = async (address, assetId) => {
  try {
    const [collectionId] = assetId.split('/');
    const assets = await fetchAddressCollection(address, collectionId);

    const asset = assets.find(a => a.assetId === assetId);
    return asset?.balance || 0;
  } catch (error) {
    console.error(`Error fetching SFT balance for ${assetId}:`, error);
    return 0;
  }
};

/**
 * Fetches market orders for an SFT
 * @param {string} assetId - Full asset ID (COLLECTION/NONCE)
 * @param {string} marketplaceId - Marketplace ID (default: Rare Canvas)
 * @returns {Promise<Array>} - Array of market orders
 */
export const fetchSFTOrders = async (assetId, marketplaceId = '417b70c0eb7a33cb') => {
  try {
    const response = await axios.get(`${KLEVER_API_BASE}/assets/${assetId}/market-orders`);
    const orders = response.data?.data?.orders || {};
    const ordersList = Array.isArray(orders) ? orders : Object.values(orders);

    // Filter by marketplace and active status
    return ordersList.filter(
      order => order.marketplaceId === marketplaceId && order.orderStatus === 0
    );
  } catch (error) {
    console.error(`Error fetching SFT orders for ${assetId}:`, error);
    return [];
  }
};

/**
 * Formats supply information for display
 * @param {Object} asset - Asset object
 * @returns {Object} - Formatted supply info
 */
export const formatSupplyInfo = (asset) => {
  if (!asset) return null;

  return {
    circulating: asset.circulatingSupply || 0,
    max: asset.maxSupply || 0,
    initial: asset.initialSupply || 0,
    percentage: asset.maxSupply > 0
      ? ((asset.circulatingSupply / asset.maxSupply) * 100).toFixed(1)
      : '100.0',
    remaining: (asset.maxSupply || 0) - (asset.circulatingSupply || 0)
  };
};

/**
 * Calculates rarity based on supply (only for SFTs)
 * Returns null for NFTs since they're inherently unique
 * @param {Object} asset - Asset object
 * @returns {string|null} - Rarity tier or null for NFTs
 */
export const calculateRarity = (asset) => {
  if (!asset) return null;

  // Only calculate rarity for SFTs, never for NFTs
  if (!isSFT(asset)) return null;

  // For SFTs, use the circulatingSupply or maxSupply to determine rarity
  // This represents how many copies of this specific SFT exist
  const supply = asset.circulatingSupply || asset.maxSupply || asset.initialSupply || 0;

  if (supply <= 1) return null; // Single copy = unique, no rarity needed
  if (supply <= 10) return 'Legendary';
  if (supply <= 100) return 'Epic';
  if (supply <= 500) return 'Rare';
  if (supply <= 1000) return 'Uncommon';
  return 'Common';
};

/**
 * Gets rarity color for UI display
 * @param {string} rarity - Rarity tier
 * @returns {string} - CSS color value
 */
export const getRarityColor = (rarity) => {
  const colors = {
    'Unique': '#ff00ff',      // Magenta
    'Legendary': '#ff6b00',   // Orange
    'Epic': '#9b59b6',        // Purple
    'Rare': '#3498db',        // Blue
    'Uncommon': '#2ecc71',    // Green
    'Common': '#95a5a6'       // Gray
  };

  return colors[rarity] || '#95a5a6';
};

/**
 * Formats price per unit for SFTs
 * @param {number} pricePerUnit - Price in smallest unit (e.g., 1000000 = 1 KLV)
 * @param {number} precision - Token precision (decimals)
 * @returns {string} - Formatted price
 */
export const formatPricePerUnit = (pricePerUnit, precision = 6) => {
  const divisor = Math.pow(10, precision);
  return (pricePerUnit / divisor).toFixed(precision);
};

/**
 * Parses asset ID into collection and nonce
 * @param {string} assetId - Full asset ID (COLLECTION/NONCE)
 * @returns {Object} - {collectionId, nonce}
 */
export const parseAssetId = (assetId) => {
  if (!assetId || !assetId.includes('/')) {
    return { collectionId: null, nonce: null };
  }

  const [collectionId, nonceStr] = assetId.split('/');
  const nonce = parseInt(nonceStr, 10);

  return { collectionId, nonce: isNaN(nonce) ? null : nonce };
};

/**
 * Builds asset ID from collection and nonce
 * @param {string} collectionId - Collection ID
 * @param {number} nonce - Token nonce
 * @returns {string} - Full asset ID
 */
export const buildAssetId = (collectionId, nonce) => {
  return `${collectionId}/${nonce}`;
};

/**
 * Fetches all SFTs owned by an address across all collections
 * @param {string} address - Wallet address
 * @returns {Promise<Array>} - Array of SFT assets with balances
 */
export const fetchUserSFTs = async (address) => {
  try {
    // Get all assets for the address
    const response = await axios.get(`${KLEVER_API_BASE}/address/${address}`);
    const account = response.data?.data?.account || {};
    const assets = account.assets || {};

    // Filter for SFTs only
    const sftList = [];

    for (const [assetId, balance] of Object.entries(assets)) {
      if (!assetId.includes('/')) continue; // Skip base tokens

      try {
        const asset = await fetchAsset(assetId);
        if (asset && isSFT(asset)) {
          sftList.push({
            ...asset,
            balance: balance || 0
          });
        }
      } catch (err) {
        console.error(`Error fetching asset ${assetId}:`, err);
      }
    }

    return sftList;
  } catch (error) {
    console.error('Error fetching user SFTs:', error);
    return [];
  }
};

/**
 * Formats royalty percentage for display
 * @param {Object} royalties - Royalties object from asset
 * @returns {string} - Formatted percentage (e.g., "5.00%")
 */
export const formatRoyalties = (royalties) => {
  if (!royalties || !royalties.percentage) return '0.00%';

  // API returns percentage as basis points (500 = 5.00%)
  const percentage = royalties.percentage / 100;
  return `${percentage.toFixed(2)}%`;
};

/**
 * Validates SFT transfer amount
 * @param {number} amount - Amount to transfer
 * @param {number} balance - User's current balance
 * @param {number} maxSupply - Asset's max supply
 * @returns {Object} - {valid, error}
 */
export const validateSFTTransfer = (amount, balance, maxSupply) => {
  if (!amount || amount <= 0) {
    return { valid: false, error: 'Amount must be greater than 0' };
  }

  if (amount > balance) {
    return { valid: false, error: 'Insufficient balance' };
  }

  if (!Number.isInteger(amount)) {
    return { valid: false, error: 'Amount must be a whole number' };
  }

  return { valid: true, error: null };
};

const sftUtils = {
  isSFT,
  isNFT,
  fetchSFTCollection,
  fetchSFTByNonce,
  fetchAsset,
  fetchSFTHolders,
  fetchAddressCollection,
  getSFTBalance,
  fetchSFTOrders,
  formatSupplyInfo,
  calculateRarity,
  getRarityColor,
  formatPricePerUnit,
  parseAssetId,
  buildAssetId,
  fetchUserSFTs,
  formatRoyalties,
  validateSFTTransfer
};

export default sftUtils;
